import React from 'react';

export type TenantUnavailableVariant =
  | 'not_found'
  | 'not_found_localhost'
  | 'unavailable';

interface TenantUnavailablePanelProps {
  variant: TenantUnavailableVariant;
}

const copy: Record<
  Exclude<TenantUnavailableVariant, 'not_found_localhost'>,
  { title: string; description: string }
> = {
  not_found: {
    title: 'Empresa não encontrada',
    description:
      'Não encontramos uma configuração para este endereço. Confira o domínio ou o link informado. Se precisar de ajuda, fale com o suporte da sua organização.',
  },
  unavailable: {
    title: 'Ambiente indisponível',
    description:
      'Não foi possível carregar as informações agora. Verifique sua conexão com a internet e tente novamente em instantes.',
  },
};

export const TenantUnavailablePanel: React.FC<TenantUnavailablePanelProps> = ({
  variant,
}) => {
  if (variant === 'not_found_localhost') {
    return (
      <div className="w-full" role="status">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
          <svg
            className="h-6 w-6 text-blue-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="login-greeting text-center">Desenvolvimento em localhost</h2>
        <div className="login-subtitle text-left text-gray-600 space-y-3 max-w-md mx-auto">
          <p>
            O navegador envia o host <code className="text-sm bg-gray-100 px-1 rounded">localhost</code>, que
            não corresponde a um tenant no banco. Escolha uma das opções:
          </p>
          <ul className="list-disc pl-5 text-sm space-y-2">
            <li>
              Abra com slug na URL, por exemplo:{' '}
              <code className="bg-gray-100 px-1 rounded text-xs break-all">
                /login?tenantSlug=baruk
              </code>{' '}
              (API em modo Development).
            </li>
            <li>
              Ou defina no <code className="text-sm bg-gray-100 px-1 rounded">.env</code> do front:{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">REACT_APP_DEV_TENANT_SLUG</code> ou{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">REACT_APP_DEV_MOCK_HOST</code>.
            </li>
            <li>
              Ou use <code className="text-xs bg-gray-100 px-1 rounded">REACT_APP_API_CLIENT_ID</code> e{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">REACT_APP_API_CLIENT_SECRET</code> para
              login só com credenciais fixas (sem resolução por host).
            </li>
          </ul>
          <p className="text-sm text-gray-500">
            Detalhes estão no README do projeto <code className="bg-gray-100 px-1 rounded">Baruk.Know.InDoc.Web</code>.
          </p>
        </div>
      </div>
    );
  }

  const { title, description } = copy[variant];

  return (
    <div className="w-full" role="alert">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 mb-4">
        <svg
          className="h-6 w-6 text-amber-700"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="login-greeting text-center">{title}</h2>
      <p className="login-subtitle text-center text-gray-600">{description}</p>
    </div>
  );
};
